import React, { useState, useEffect, useRef, useContext } from 'react';
import { API } from 'aws-amplify';
import { getProduct, listProducts } from '../graphql/queries';
import { CartContext } from '../components/contexts/CartContext';
import { WishlistContext } from '../components/contexts/WishlistContext';
import { convertCentsToDollar } from '../utils';
import config from '../aws-exports';
import { Notification, Loading } from 'element-react';
import styled from 'styled-components';
import { Link, withRouter } from 'react-router-dom';
import ProductCarousel from '../components/recommendations/ProductCarousel';
import FrequentlyBoughtTogether from '../components/recommendations/FrequentlyBoughtTogether';
import { addRecentlyViewed, getRecentlyViewed } from '../utils/recentlyViewedUtils';

const {
  aws_user_files_s3_bucket_region: region,
  aws_user_files_s3_bucket: bucket
} = config;

const ProductPage = ({ match, history }) => {
  const { productId } = match.params;
  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [recentlyViewed, setRecentlyViewed] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeSlide, setActiveSlide] = useState(0);
  const carouselRef = useRef(null);

  const { addProduct, cartItems, increase, decrease, removeProduct } = useContext(CartContext);
  const { isInWishlist, toggleWishlist } = useContext(WishlistContext);

  useEffect(() => {
    const fetchProductAndRecommendations = async () => {
      try {
        setLoading(true);
        const result = await API.graphql({
          query: getProduct,
          variables: { id: productId },
          authMode: "API_KEY"
        });

        if (result.data.getProduct) {
          const currentProd = result.data.getProduct;
          setProduct(currentProd);

          // Save current product to recently viewed LocalStorage
          addRecentlyViewed(currentProd);

          // Get other recently viewed items (excluding current)
          const recentList = getRecentlyViewed().filter((p) => p.id !== productId);
          setRecentlyViewed(recentList);

          // Fetch related products catalog
          try {
            const listRes = await API.graphql({
              query: listProducts,
              variables: { limit: 12 },
              authMode: "API_KEY"
            });
            const allItems = listRes.data.listProducts.items || [];
            const filteredRelated = allItems.filter((item) => item.id !== productId);
            setRelatedProducts(filteredRelated);
          } catch (relErr) {
            console.error('Error fetching related products', relErr);
          }
        } else {
          setError('Product not found');
        }
      } catch (err) {
        console.error('Error fetching product', err);
        setError('Failed to fetch product details.');
      } finally {
        setLoading(false);
      }
    };

    fetchProductAndRecommendations();
  }, [productId]);

  // Track which slide is visible via scroll position
  const handleCarouselScroll = () => {
    if (!carouselRef.current) return;
    const el = carouselRef.current;
    const slideWidth = el.offsetWidth;
    const idx = Math.round(el.scrollLeft / slideWidth);
    setActiveSlide(idx);
  };

  const scrollToSlide = (idx) => {
    if (!carouselRef.current) return;
    carouselRef.current.scrollTo({
      left: carouselRef.current.offsetWidth * idx,
      behavior: 'smooth',
    });
    setActiveSlide(idx);
  };

  if (loading) {
    return <Loading fullscreen={true} text="Loading product details..." />;
  }

  if (error || !product) {
    return (
      <ErrorContainer>
        <ErrorTitle>Oops! Product Not Found</ErrorTitle>
        <ErrorMessage>{error || "We couldn't find the product you were looking for."}</ErrorMessage>
        <BackBtn to="/">Back to Sweets & Grocery list</BackBtn>
      </ErrorContainer>
    );
  }

  const imageUrl = `https://${bucket}.s3.${region}.amazonaws.com/public/${product.file.key}`;
  const cartItem = cartItems.find(item => item.id === product.id);
  const inCart = !!cartItem;

  const notifyAdded = () =>
    Notification({ title: 'Success', message: 'Added to cart!', type: 'success' });

  const notifyRemoved = (msg = 'Removed from cart!') =>
    Notification({ title: 'Success', message: msg, type: 'success' });

  const handleDecrease = () => {
    if (cartItem.quantity > 1) {
      decrease(product);
      notifyRemoved('Quantity decreased');
    } else {
      removeProduct(product);
      notifyRemoved('Removed from cart!');
    }
  };

  const isFav = product ? isInWishlist(product.id) : false;

  const handleWishlist = () => {
    if (!product) return;
    toggleWishlist(product);
    Notification({
      title: 'success',
      message: isFav ? 'Removed from Wishlist' : 'Saved to Wishlist & Buy Again!',
      type: 'success'
    });
  };

  const stock = product.stockQuantity !== undefined && product.stockQuantity !== null ? product.stockQuantity : 50;
  const isOutOfStock = stock <= 0;
  const isLowStock = stock > 0 && stock <= 5;

  return (
    <PageWrapper>
      <BackButton onClick={() => history.goBack()}>
        <span role="img" aria-label="back">⬅️</span> Back
      </BackButton>

      <ContentGrid>
        <ImageSection>
          {/* ---------- swipe carousel ---------- */}
          {(() => {
            const slides = [
              { pos: 'center', label: 'Front view' },
              { pos: 'top',    label: 'Top view' },
              { pos: 'bottom', label: 'Detail view' },
            ];
            return (
              <CarouselWrap>
                <CarouselTrack
                  ref={carouselRef}
                  onScroll={handleCarouselScroll}
                >
                  {slides.map((slide, i) => (
                    <CarouselSlide key={i}>
                      <SlideImg
                        src={imageUrl}
                        alt={`${product.description} — ${slide.label}`}
                        $pos={slide.pos}
                        loading={i === 0 ? 'eager' : 'lazy'}
                      />
                    </CarouselSlide>
                  ))}
                </CarouselTrack>

                {/* dot indicators */}
                <DotRow>
                  {slides.map((_, i) => (
                    <Dot
                      key={i}
                      $active={activeSlide === i}
                      onClick={() => scrollToSlide(i)}
                      aria-label={`Go to slide ${i + 1}`}
                    />
                  ))}
                </DotRow>
              </CarouselWrap>
            );
          })()}
        </ImageSection>

        <DetailsSection>
          {product.market && (
            <CategoryLink to={`/markets/${product.market.id}`}>
              📂 {product.market.name}
            </CategoryLink>
          )}

          <ProductTitle>{product.description}</ProductTitle>
          
          <PriceRow>
            <PriceTag>${convertCentsToDollar(product.price)}</PriceTag>
            <EachText>each</EachText>
          </PriceRow>

          <Divider />

          <MetaInfo>
            <MetaRow>
              <MetaLabel>Availability:</MetaLabel>
              <MetaValue>
                {isOutOfStock ? (
                  <span style={{ color: '#d32f2f', fontWeight: '800' }}>🔴 Out of Stock</span>
                ) : isLowStock ? (
                  <span style={{ color: '#c96a12', fontWeight: '800' }}>⚠️ Only {stock} left in stock!</span>
                ) : (
                  <span style={{ color: '#0a8f57', fontWeight: '800' }}>🟢 In Stock ({stock} available)</span>
                )}
              </MetaValue>
            </MetaRow>
            <MetaRow>
              <MetaLabel>Delivery:</MetaLabel>
              <MetaValue>
                {product.shipped ? '🚚 Daily doorstep delivery' : '📧 Instant email/digital delivery'}
              </MetaValue>
            </MetaRow>

            <MetaRow>
              <MetaLabel>Added on:</MetaLabel>
              <MetaValue>
                {product.createdAt 
                  ? new Date(product.createdAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
                  : 'N/A'
                }
              </MetaValue>
            </MetaRow>
          </MetaInfo>

          <Divider />

          <ActionBox>
            {isOutOfStock ? (
              <BuyButton disabled style={{ background: '#e2e8f0', color: '#a0aec0', cursor: 'not-allowed' }}>
                Out of Stock
              </BuyButton>
            ) : inCart ? (
              <CartControls>
                <QtyBtn onClick={handleDecrease}>-</QtyBtn>
                <QtyDisplay>{cartItem.quantity}</QtyDisplay>
                <QtyBtn onClick={() => { increase(product); notifyAdded(); }}>+</QtyBtn>
                <AddMoreIndicator>in cart</AddMoreIndicator>
              </CartControls>
            ) : (
              <BuyButton onClick={() => { addProduct(product); notifyAdded(); }}>
                Add to Cart
              </BuyButton>
            )}

            <FavActionBtn type="button" onClick={handleWishlist} $active={isFav}>
              {isFav ? '❤️ Saved' : '🤍 Wishlist'}
            </FavActionBtn>
          </ActionBox>
        </DetailsSection>
      </ContentGrid>

      {/* Recommendations & Bundle Section */}
      <FrequentlyBoughtTogether
        mainProduct={product}
        complementaryProducts={relatedProducts.slice(0, 2)}
      />

      <ProductCarousel
        title="You Might Also Like"
        subtitle="Recommended items based on store & category"
        products={relatedProducts}
      />

      <ProductCarousel
        title="Recently Viewed Products"
        subtitle="Items you looked at recently"
        products={recentlyViewed}
      />

      {/* Mobile Sticky Floating Action Bar (< 600px) */}
      <MobileFloatingBar>
        <MobilePriceWrap>
          <MobilePriceLabel>Price</MobilePriceLabel>
          <MobilePriceVal>${convertCentsToDollar(product.price)}</MobilePriceVal>
        </MobilePriceWrap>

        {isOutOfStock ? (
          <BuyButton disabled style={{ background: '#e2e8f0', color: '#a0aec0', cursor: 'not-allowed', padding: '8px 16px' }}>
            Out of Stock
          </BuyButton>
        ) : inCart ? (
          <CartControls>
            <QtyBtn onClick={handleDecrease}>-</QtyBtn>
            <QtyDisplay>{cartItem.quantity}</QtyDisplay>
            <QtyBtn onClick={() => { increase(product); notifyAdded(); }}>+</QtyBtn>
          </CartControls>
        ) : (
          <BuyButton onClick={() => { addProduct(product); notifyAdded(); }}>
            Add to Cart
          </BuyButton>
        )}
      </MobileFloatingBar>
    </PageWrapper>
  );
};

/* ---------- styles ---------- */
const PageWrapper = styled.div`
  max-width: 1100px;
  margin: 40px auto;
  padding: 0 20px;
`;

const BackButton = styled.button`
  background: transparent;
  border: 1px solid rgba(31, 45, 61, 0.15);
  color: #24313f;
  font-weight: 700;
  font-size: 0.95rem;
  padding: 10px 20px;
  border-radius: 999px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 28px;
  transition: background 0.15s ease, border-color 0.15s ease;

  &:hover {
    background: rgba(31, 45, 61, 0.05);
    border-color: rgba(31, 45, 61, 0.25);
  }
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 40px;

  @media screen and (min-width: 768px) {
    grid-template-columns: 1.1fr 1fr;
  }
`;

const ImageSection = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-start;
`;

/* ---- carousel ---- */
const CarouselWrap = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const CarouselTrack = styled.div`
  display: flex;
  overflow-x: scroll;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior-x: contain;
  scrollbar-width: none;
  border-radius: 20px;
  gap: 0;

  &::-webkit-scrollbar { display: none; }
`;

const CarouselSlide = styled.div`
  flex: 0 0 100%;
  scroll-snap-align: start;
  aspect-ratio: 1.1 / 1;
  background: #fff;
  border: 1px solid rgba(31, 45, 61, 0.08);
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(31, 45, 61, 0.05);
  display: grid;
  place-items: center;
  padding: 24px;
`;

const SlideImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  object-position: ${({ $pos }) => $pos || 'center'};
  transition: transform 0.35s ease;

  &:hover {
    transform: scale(1.04);
  }
`;

const DotRow = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
`;

const Dot = styled.button`
  width: ${({ $active }) => ($active ? '24px' : '8px')};
  height: 8px;
  border-radius: 999px;
  background: ${({ $active }) => ($active ? '#01bf71' : 'rgba(31,45,61,0.18)')};
  border: none;
  padding: 0;
  cursor: pointer;
  transition: width 0.25s ease, background 0.25s ease;

  &:hover {
    background: ${({ $active }) => ($active ? '#01bf71' : 'rgba(31,45,61,0.35)')};
  }
`;

const DetailsSection = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
`;

const CategoryLink = styled(Link)`
  text-decoration: none;
  font-size: 0.88rem;
  font-weight: 700;
  color: #01bf71;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 12px;
  display: inline-flex;
  align-items: center;
  gap: 4px;

  &:hover {
    color: #04a763;
  }
`;

const ProductTitle = styled.h1`
  margin: 0 0 16px;
  font-size: clamp(1.8rem, 3.5vw, 2.5rem);
  font-weight: 800;
  color: #1f2d3d;
  line-height: 1.2;
  text-transform: capitalize;
`;

const PriceRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 24px;
`;

const PriceTag = styled.span`
  font-size: 2rem;
  font-weight: 800;
  color: #0a8f57;
`;

const EachText = styled.span`
  color: #8a99a8;
  font-size: 1rem;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid rgba(31, 45, 61, 0.08);
  margin: 20px 0;
  width: 100%;
`;

const MetaInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const MetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 0.95rem;
`;

const MetaLabel = styled.span`
  color: #8a99a8;
  font-weight: 700;
  width: 100px;
`;

const MetaValue = styled.span`
  color: #24313f;
  font-weight: 500;
`;

const ActionBox = styled.div`
  margin-top: 16px;
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
`;

const FavActionBtn = styled.button`
  background: ${props => (props.$active ? 'rgba(239, 83, 80, 0.1)' : 'rgba(31, 45, 61, 0.05)')};
  color: ${props => (props.$active ? '#d32f2f' : '#46586a')};
  border: 1.5px solid ${props => (props.$active ? 'rgba(239, 83, 80, 0.3)' : 'rgba(31, 45, 61, 0.12)')};
  font-size: 0.95rem;
  font-weight: 700;
  padding: 14px 22px;
  border-radius: 999px;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: ${props => (props.$active ? 'rgba(239, 83, 80, 0.16)' : 'rgba(31, 45, 61, 0.08)')};
    transform: translateY(-1px);
  }
`;

const BuyButton = styled.button`
  background: #01bf71;
  color: #fff;
  border: none;
  font-size: 1.1rem;
  font-weight: 700;
  padding: 16px 40px;
  border-radius: 999px;
  cursor: pointer;
  box-shadow: 0 10px 24px rgba(1, 191, 113, 0.35);
  transition: background 0.15s ease, transform 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    background: #04a763;
    transform: translateY(-2px);
    box-shadow: 0 14px 30px rgba(1, 191, 113, 0.42);
  }
`;

const CartControls = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
`;

const QtyBtn = styled.button`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: 1.5px solid rgba(1, 191, 113, 0.55);
  background: transparent;
  color: #0a8f57;
  font-size: 1.3rem;
  font-weight: 700;
  cursor: pointer;
  display: grid;
  place-items: center;
  transition: background 0.15s ease;

  &:hover {
    background: rgba(1, 191, 113, 0.08);
  }
`;

const QtyDisplay = styled.span`
  font-size: 1.3rem;
  font-weight: 800;
  color: #1f2d3d;
  min-width: 24px;
  text-align: center;
`;

const AddMoreIndicator = styled.span`
  font-size: 0.85rem;
  font-weight: 700;
  color: #8a99a8;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  background: rgba(31, 45, 61, 0.05);
  padding: 4px 10px;
  border-radius: 999px;
`;

const ErrorContainer = styled.div`
  text-align: center;
  padding: 80px 20px;
`;

const ErrorTitle = styled.h2`
  color: #1f2d3d;
  font-size: 1.8rem;
  font-weight: 800;
  margin-bottom: 12px;
`;

const ErrorMessage = styled.p`
  color: #8a99a8;
  margin-bottom: 24px;
  font-size: 1.05rem;
`;

const BackBtn = styled(Link)`
  color: #0a8f57;
  font-weight: 700;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const MobileFloatingBar = styled.div`
  position: fixed;
  bottom: calc(60px + env(safe-area-inset-bottom, 0px));
  left: 0;
  right: 0;
  background: rgba(255, 255, 255, 0.96);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border-top: 1px solid rgba(31, 45, 61, 0.1);
  padding: 10px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  z-index: 998;
  box-shadow: 0 -4px 16px rgba(31, 45, 61, 0.08);

  @media screen and (min-width: 600px) {
    display: none;
  }

  ${BuyButton} {
    padding: 10px 24px;
    font-size: 0.95rem;
    box-shadow: 0 4px 14px rgba(1, 191, 113, 0.3);
  }

  ${QtyBtn} {
    width: 36px;
    height: 36px;
    font-size: 1.1rem;
  }

  ${QtyDisplay} {
    font-size: 1.1rem;
  }
`;

const MobilePriceWrap = styled.div`
  display: flex;
  flex-direction: column;
`;

const MobilePriceLabel = styled.span`
  font-size: 0.72rem;
  font-weight: 700;
  color: #8a99a8;
  text-transform: uppercase;
`;

const MobilePriceVal = styled.span`
  font-size: 1.15rem;
  font-weight: 800;
  color: #0a8f57;
`;

export default withRouter(ProductPage);
